import { useEffect, useState, type ReactNode, type Ref } from 'react'
import { Chip, Link, Spinner, Table } from '@heroui/react'
import { api } from '../lib/api'
import { latestRead } from '../lib/latestRead'
import { explain } from '../lib/staffErrors'
import type { BookingBy, BookingCounts, BookingReport, Branch } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import {
  BOOKING_BYS,
  BOOKING_COLUMNS,
  BY_KEY,
  CAME_PARTS,
  onTimeBand,
  LEAD_KEYS,
  bookingDrill,
  bookingLines,
  bookingTiles,
  bookingsSheet,
  columnValue,
  latenessText,
  levelOf,
  waitedMedian,
  type BookingLine,
} from './bookingsCalc'
import { daysIn, duration, figuresSheet, formatValue, group, isoToday, periodRange, withBranch, type PeriodId } from './insightCalc'
import { ExportMenu, PeriodMenu } from './ReportBar'
import { Bars, DurText, NoData, Tile, type MenuItem } from './insightVisuals'
import { Notice, Pick } from './kit'
import type { TicketFilters } from './ticketReport'
import { download, xlsx, type XSheet } from './xlsx'

type Names = Partial<Record<keyof TicketFilters, string>>

/**
 * What became of a branch's confirmed bookings: the same branch and period as
 * the figures, five headline figures, three visuals and a table by one grouping.
 * Only a booking that came has a ticket, so only Came opens tickets.
 */
export function Bookings({
  branches,
  branchId,
  onBranch,
  period,
  onPeriod,
  custom,
  onCustom,
  flash,
  lead,
  onMail,
  active,
  onRead,
  onDrill,
  boxRef,
}: {
  branches: Branch[]
  branchId: string
  onBranch: (id: string) => void
  period: PeriodId
  onPeriod: (p: PeriodId) => void
  custom: [string, string]
  onCustom: (c: [string, string]) => void
  /** Bumped when the period was set for this page, so the change is seen. */
  flash?: number
  /** First in the bar: the Questions button where the rail is folded away. */
  lead: ReactNode
  /** Opens the reports by email. */
  onMail: () => void
  /** Read only while on screen; kept, not re-read, on return unless the period includes today. */
  active: boolean
  onRead: (branchId: string, from: string, to: string, r: BookingReport) => void
  onDrill: (filters: TicketFilters, names: Names) => void
  boxRef?: Ref<HTMLDivElement>
}) {
  const { lang, say } = useStaffText()
  const today = isoToday()
  const [by, setBy] = useState<BookingBy>('service')
  const [got, setGot] = useState<{ query: string; branchId: string; from: string; to: string; report: BookingReport } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const [from, to] = periodRange(period, today, custom)
  const badDates = period === 'custom' && (!from || !to || from > to)
  const query = [branchId, from, to, by].join('|')

  useEffect(() => {
    // An aborted read never settles: whatever stops here leaves Reading… off.
    if (!active || !branchId || badDates) return setBusy(false)
    if (got?.query === query && to < isoToday()) return setBusy(false)
    setBusy(true)
    setError('')
    return latestRead((signal) => api.bookingReport(branchId, from, to, by, signal), {
      done: (report) => {
        setGot({ query, branchId, from, to, report })
        onRead(branchId, from, to, report)
      },
      failed: (err) => {
        setError(explain(err, lang, 'common.unreachable'))
        setGot(null)
      },
      settled: () => setBusy(false),
    })
  }, [active, query, badDates, lang]) // eslint-disable-line react-hooks/exhaustive-deps

  const r = got?.report ?? null
  const branch = branches.find((b) => b.id === branchId)
  const shownBy = r?.by ?? by
  const lines: BookingLine[] = r ? bookingLines(r, lang, say) : []
  // Weekday rows over more than a week are totals of several, as on Figures.
  const days = r ? daysIn(r.range.from, r.range.to) : 0
  const weekdayNote = shownBy === 'weekday' && days > 7 ? (days % 7 === 0 ? say('insight.nWeeks', { n: days / 7 }) : say('insight.nDays', { n: days })) : undefined
  const about = [branch?.name ?? '', got ? (got.from === got.to ? got.from : say('reports.periodRange', { from: got.from, to: got.to })) : ''].filter(Boolean).join('; ')
  const minutes = say('insight.minutes')
  const save = (sheet: XSheet, title: string, branchCol = true) =>
    download(xlsx(branchCol ? withBranch(sheet, say('common.branch'), branch?.name ?? '') : sheet), `${branch?.name ?? ''} - ${title} - ${got && got.from !== got.to ? `${got.from} ${got.to}` : (got?.from ?? '')}`.replace(/[\\/:*?"<>|]/g, ' ') + '.xlsx')
  const exportMenu = (run: () => void): MenuItem[] => [{ label: say('insight.exportExcel'), run }]
  /** A visual's bars as a sheet; `head` names what the numbers count. */
  const exportBars = (title: string, head: string, labels: string[], values: number[]) =>
    save(figuresSheet(title, about, ['', head], labels.map((l, i) => [l, values[i] ?? 0]), ['int'], minutes), title)

  // The tickets of the bookings that came, over the period the figures were read for.
  const goTo = (rowBy: BookingBy | null, key?: string, label?: string): (() => void) | null => {
    if (!got) return null
    const f = bookingDrill({ branchId: got.branchId, from: got.from, to: got.to }, rowBy, key)
    if (!f) return null
    const names: Names = rowBy === 'service' && label ? { booked: label } : {}
    return () => onDrill(f, names)
  }

  const exportTable = () => r && save(bookingsSheet(r, lines, say('bookingsReport.tab'), about, say(BY_KEY[shownBy]), say, minutes), say('bookingsReport.tab'))

  const cameLabels = CAME_PARTS.map((p) => say(p.label))
  const leadLabels = LEAD_KEYS.map((k) => say(k))
  /** A line's cells: the figures, Came a link to its tickets where they can be told apart. */
  const cells = (c: BookingCounts, rowBy: BookingBy | null, key?: string, label?: string) =>
    BOOKING_COLUMNS.map((col) => {
      const value = got ? columnValue(col, c, got.report, key) : col.of(c)
      const text = formatValue(value, col.fmt, lang)
      // A zero opens nothing, as on Figures.
      const go = col.drills && value ? goTo(rowBy, key, label) : null
      return (
        <Table.Cell key={col.label} className="insight-grid__num insight-num">
          {go ? (
            <Link className="insight-cellgo" aria-label={`${text}, ${say('insight.showTickets')}`} onPress={go}>
              {text}
            </Link>
          ) : (
            text
          )}
        </Table.Cell>
      )
    })

  return (
    <div className="insight" ref={boxRef} style={active ? undefined : { display: 'none' }}>
      <div className="insight-top">
        <div className="insight-top__row">
          {lead}
          <Pick label={say('common.branch')} caption={say('common.branch')} value={branchId} onChange={onBranch} options={branches.map((b) => ({ value: b.id, label: b.name }))} />
          <PeriodMenu period={period} onPeriod={onPeriod} custom={custom} onCustom={onCustom} today={today} bad={badDates} flash={flash} />
          <span className="insight-spacer" />
          <div className="insight-actions">
            {busy && (
              <span className="insight-busy">
                <Spinner size="sm" />
                {say('common.reading')}
              </span>
            )}
            <ExportMenu
              items={[
                { label: say('reports.excel'), icon: 'table', run: r ? exportTable : undefined },
                'rule',
                { label: say('reportMail.title'), icon: 'mail', run: onMail },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="insight-body">
        <main className="insight-main" data-insight-scroller>
          {badDates ? (
            <p className="insight-problem" role="alert">
              {say('insight.badDates')}
            </p>
          ) : error ? (
            <Notice>{error}</Notice>
          ) : (
            r &&
            (r.booked === 0 ? (
              // One empty state, not five zeros and four "nothing to show".
              <p className="insight-nodata">{say('bookingsReport.none')}</p>
            ) : (
              <>
                <div className="insight-kpis insight-kpis--5">
                  {bookingTiles(r, r.range, r.movesSince, lang).map((tile) => {
                    const go = tile.drills && tile.value ? goTo(null) : null
                    // Each share on its own line with its base: the tiles are not of one base and do not add up.
                    const body = (
                      <>
                        <span className="insight-kpi__label">{say(tile.label)}</span>
                        <strong className="insight-kpi__value">{tile.value === null ? '—' : group(tile.value)}</strong>
                        {tile.notes.map((n) => (
                          <span key={n.key} className="insight-kpi__note">
                            {say(n.key, n.vars)}
                          </span>
                        ))}
                      </>
                    )
                    return go ? (
                      <button key={tile.key} type="button" className="insight-kpi insight-kpi--go" title={say('insight.showTickets')} onClick={go}>
                        {body}
                      </button>
                    ) : (
                      <div key={tile.key} className="insight-kpi">
                        {body}
                      </div>
                    )
                  })}
                </div>

                <div className="insight-canvas">
                  <Tile
                    id="bkcame"
                    title={say('bookingsReport.against')}
                    info={say('bookingsReport.againstNote', { early: onTimeBand(r), late: onTimeBand(r) })}
                    focused={false}
                    onBack={() => {}}
                    menu={exportMenu(() =>
                      exportBars(
                        say('bookingsReport.against'),
                        say('bookingsReport.came'),
                        cameLabels,
                        CAME_PARTS.map((p) => r[p.key]),
                      ),
                    )}
                  >
                    {r.arrived === 0 ? (
                      <NoData />
                    ) : (
                      <>
                        <Bars
                          marks={CAME_PARTS.map((p, i) => ({ key: p.key, label: cameLabels[i] ?? '', value: r[p.key] }))}
                          fmt={(n) => group(n ?? 0)}
                          label={say('bookingsReport.against')}
                        />
                        <p className="insight-small">{say('bookingsReport.typically', { value: latenessText(r, lang, say) })}</p>
                      </>
                    )}
                  </Tile>
                  <Tile
                    id="bklead"
                    title={say('bookingsReport.ahead')}
                    focused={false}
                    onBack={() => {}}
                    menu={exportMenu(() => exportBars(say('bookingsReport.ahead'), say('bookingsReport.booked'), leadLabels, [...r.lead]))}
                  >
                    {r.booked === 0 ? (
                      <NoData />
                    ) : (
                      <Bars marks={leadLabels.map((l, i) => ({ key: String(i), label: l, value: r.lead[i] ?? 0 }))} fmt={(n) => group(n ?? 0)} label={say('bookingsReport.ahead')} />
                    )}
                  </Tile>
                  <Tile
                    id="bkwaited"
                    title={say('bookingsReport.waited')}
                    info={say('reports.levelNote')}
                    focused={false}
                    onBack={() => {}}
                    menu={exportMenu(() =>
                      save(
                        figuresSheet(
                          say('bookingsReport.waited'),
                          about,
                          [say('common.branch'), say('reports.waitingMedian'), say('reports.serviceLevel')],
                          [[branch?.name ?? '', waitedMedian(r), levelOf(r)]],
                          ['dur', 'pct'],
                          minutes,
                        ),
                        say('bookingsReport.waited'),
                        false,
                      ),
                    )}
                  >
                    {r.waited.count === 0 && r.serviceLevel.measured === 0 ? (
                      <NoData />
                    ) : (
                      <div className="insight-stats">
                        <div className="insight-stat">
                          <span className="insight-kpi__label">{say('reports.waitingMedian')}</span>
                          <strong className="insight-kpi__value">{waitedMedian(r) === null ? '—' : <DurText sec={waitedMedian(r)!} lang={lang} />}</strong>
                          {r.waited.count > 0 && <span className="insight-kpi__note">{say('reports.ninetieth', { value: duration(r.waited.p90Sec, lang) })}</span>}
                        </div>
                        <div className="insight-stat">
                          <span className="insight-kpi__label">{say('reports.serviceLevel')}</span>
                          <strong className="insight-kpi__value">{formatValue(levelOf(r), 'pct', lang)}</strong>
                          {r.serviceLevel.measured > 0 && <span className="insight-kpi__note">{say('reports.ofDecided', { n: r.serviceLevel.measured })}</span>}
                        </div>
                      </div>
                    )}
                  </Tile>

                  <Tile
                    id="bktable"
                    title={say('bookingsReport.tab')}
                    focused={false}
                    onBack={() => {}}
                    tools={<Pick label={say('insight.rows')} value={by} onChange={(v) => setBy(v as BookingBy)} options={BOOKING_BYS.map((b) => ({ value: b, label: say(BY_KEY[b]) }))} />}
                    menu={exportMenu(exportTable)}
                  >
                    {lines.length === 0 ? (
                      <NoData />
                    ) : (
                      <Table variant="secondary" className="insight-grid">
                        <Table.ScrollContainer>
                          <Table.Content aria-label={say('bookingsReport.tab')}>
                            <Table.Header>
                              <Table.Column id="by" isRowHeader>
                                {say(BY_KEY[shownBy])}
                                {weekdayNote && (
                                  <Chip size="sm" variant="tertiary" className="insight-cornernote">
                                    {weekdayNote}
                                  </Chip>
                                )}
                              </Table.Column>
                              {BOOKING_COLUMNS.map((c) => (
                                <Table.Column key={c.label} id={c.label} className="insight-grid__num">
                                  {say(c.label)}
                                </Table.Column>
                              ))}
                            </Table.Header>
                            <Table.Body>
                              {lines.map((l) => (
                                <Table.Row key={l.key || l.label} id={l.key || l.label}>
                                  <Table.Cell>{l.label}</Table.Cell>
                                  {cells(l.c, shownBy, l.key, l.label)}
                                </Table.Row>
                              ))}
                              <Table.Row id="__total" className="insight-total">
                                <Table.Cell>{say('insight.total')}</Table.Cell>
                                {cells(r, null)}
                              </Table.Row>
                            </Table.Body>
                          </Table.Content>
                        </Table.ScrollContainer>
                      </Table>
                    )}
                  </Tile>
                </div>
              </>
            ))
          )}
        </main>
      </div>
    </div>
  )
}

